import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import Loader from "@/app/components/common/Loader";

const PROTECTED_MATCHERS = [
  "/seller",
  "/customer/home",
  "/seller/home",
  "/seller/dashboard",
  "/categories",
  "/products",
  "/orders",
  "/invoices",
  "/cart",
  "/payment/success",
  "/help",
  "/profile",
  "/wallet",
  "/wishlist",
];

const normalizePath = (pathname: string) => {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }

  return pathname;
};

const isProtectedRoute = (pathname: string) => {
  const normalized = normalizePath(pathname);

  if (
    normalized.startsWith("/auth/") ||
    normalized === routes.seller.signup ||
    normalized === routes.landing
  ) {
    return false;
  }

  return PROTECTED_MATCHERS.some(
    (matcher) => normalized === matcher || normalized.startsWith(`${matcher}/`),
  );
};

export default function RouteGuard({
  children,
}: React.PropsWithChildren) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status } = useAuth();

  useEffect(() => {
    if (status !== "unauthenticated" || !pathname) {
      return;
    }

    if (!isProtectedRoute(pathname)) {
      return;
    }

    const query = searchParams.toString();
    const callbackPath = query ? `${pathname}?${query}` : pathname;
    const loginUrl = new URL(routes.auth.login, window.location.origin);
    loginUrl.searchParams.set("callbackUrl", callbackPath);

    navigate(`${loginUrl.pathname}${loginUrl.search}`, { replace: true });
  }, [navigate, pathname, searchParams, status]);

  if (status === "loading" && pathname && isProtectedRoute(pathname)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  return children;
}
